import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCalendarApp, ScheduleXCalendar } from "@schedule-x/react";
import { createViewMonthGrid, createViewWeek, createViewMonthAgenda } from "@schedule-x/calendar";
import { createCalendarControlsPlugin } from "@schedule-x/calendar-controls";
import { translations } from "@schedule-x/translations";
import "@schedule-x/theme-default/dist/index.css";
import styles from "./CalendarPage.module.css";
import AccountDataService from "../../Services/accountDataService";

import horseIcon from "../../Assets/Icons/horse_black.png";
import clientIcon from "../../Assets/Icons/client_black.png";
import serviceIcon from "../../Assets/Icons/service_black.png";
import { useModal } from "../../Context/ModalContext";
import UpsertServiceOccurenceModal from "../../Components/Modal/UpsertServiceOccurenceModal";
import { useLocation } from "react-router-dom";

const fmtDateTime = (d, locale) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dt);
  } catch {
    return String(d);
  }
};

const addMinutes = (date, minutes) => new Date(new Date(date).getTime() + minutes * 60000);

/** Clé de secours si on ne trouve pas d'id dans le DOM */
const buildKey = (title, startISO, endISO) => `${title}__${startISO}__${endISO || ""}`;

/** Transforme le DTO backend -> events Schedule‑X + map d’enrichissement */
const mapDtoToEvents = (dto, locale) => {
  const byId = new Map(); // id -> meta
  const byKey = new Map(); // fallback (title+start+end) -> meta
  const events = [];

  const list = Array.isArray(dto?.serviceOccurences) ? dto.serviceOccurences : [];

  const now = new Date();

  for (const o of list) {
    const id = String(o.serviceOccurenceExternalId || crypto.randomUUID());
    const title = o.serviceName || "(Service)";
    const start = new Date(o.serviceDate);
    const end = addMinutes(start, 60);

    const startISO = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(
      start.getDate(),
    ).padStart(
      2,
      "0",
    )} ${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;

    const endISO = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(
      end.getDate(),
    ).padStart(
      2,
      "0",
    )} ${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;

    const isPast = end < now;

    const meta = {
      id,
      title,
      clientName: o.clientName || "",
      horseName: o.horseName || "",
      serviceName: o.serviceName || "",
      serviceDate: o.serviceDate,
      startISO,
      endISO,
      whenLabel: fmtDateTime(o.serviceDate, locale),
      isPast,
    };

    byId.set(id, meta);
    byKey.set(buildKey(title, startISO, endISO), meta);

    events.push({
      id,
      title,
      start: startISO,
      end: endISO,
      _options: {
        additionalClasses: [isPast ? "is-past" : "is-future"],
      },
    });
  }

  // En‑tête : un seul des trois peut être présent
  let headerName = "";
  let headerType = null;
  if (dto?.clientName) {
    headerName = dto.clientName;
    headerType = "client";
  } else if (dto?.horseName) {
    headerName = dto.horseName;
    headerType = "horse";
  } else if (dto?.serviceName) {
    headerName = dto.serviceName;
    headerType = "service";
  }

  return { events, byId, byKey, headerName, headerType };
};

export default function CalendarPage() {
  const location = useLocation();
  const { paramClientExternalId, paramHorseExternalId, paramUserServiceExternalId } =
    location.state || {};
  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => (i18n.language?.toLowerCase().startsWith("fr") ? "fr-FR" : "en-US"),
    [i18n.language],
  );

  const { openModal } = useModal();
  const serviceModalRef = useRef(null);
  const controls = useMemo(() => createCalendarControlsPlugin(), []);
  const shellRef = useRef(null);

  // ---- état
  const [tip, setTip] = useState(null); // { title, client, horse, when }
  const [anchor, setAnchor] = useState(null); // { left, top, below: boolean }
  const [headerName, setHeaderName] = useState("");
  const [headerType, setHeaderType] = useState(null);
  const eventsByIdRef = useRef(new Map());
  const eventsByKeyRef = useRef(new Map());

  // ---- app Schedule‑X (événements initialement vides)
  const calendar = useCalendarApp(
    {
      views: [createViewMonthGrid(), createViewWeek(), createViewMonthAgenda()],
      defaultView: "monthGrid",
      locale,
      weekOptions: { start: "monday" },
      translations,
      events: [],
      callbacks: {
        onEventClick: (ev) => {
          const meta = eventsByIdRef.current.get(String(ev.id));
          setTimeout(() => {
            openModal({
              title: i18n.t("BoardServiceClient.ServiceOccurenceUpdate"),
              form: "formServiceOccurence",
              wideMode: true,
              hideSaveButton: false,
              hideSaveAndCloseButton: false,
              hideGenerateReportButton: false,
              generateReportLabel: i18n.t("Modal.GenerateReport.ServiceReport"),
              onSave: async () => {
                const isValid = await serviceModalRef.current.validate();
                if (!isValid) return;
                return await serviceModalRef.current.save();
              },
              canClose: async () => {
                const isDirty = serviceModalRef.current?.isDirty?.();
                if (!isDirty) return true;

                return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
              },
              onClose: () => {},
              content: (
                <UpsertServiceOccurenceModal
                  key={meta.id}
                  ref={serviceModalRef}
                  serviceOccurenceExternalId={meta.id}
                  onSave={async () => {
                    window.location.reload();
                  }}
                />
              ),
            });
          }, 0);
        },
        onClickDate: (date) => {
          controls.setDate(date);
        },
        onClickPlusEvents: (date) => {
          controls.setView("week");
          controls.setDate(date);
        },
      },
    },
    // ✅ plugins en 2ᵉ argument
    [controls],
  );

  useEffect(() => {
    const hide = () => {
      setTip(null);
      setAnchor(null);
    };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const dto = await AccountDataService.GetCalendarData(
          paramClientExternalId,
          paramHorseExternalId,
          paramUserServiceExternalId,
        );
        if (!alive) return;

        const { events, byId, byKey, headerName, headerType } = mapDtoToEvents(dto, locale);
        eventsByIdRef.current = byId;
        eventsByKeyRef.current = byKey;
        setHeaderName(headerName);
        setHeaderType(headerType);

        calendar?.events?.set?.(events);
      } catch (err) {
        console.error("Calendar fetch failed:", err);
        eventsByIdRef.current = new Map();
        eventsByKeyRef.current = new Map();
        setHeaderName("");
        setHeaderType(null);
        calendar?.events?.set?.([]);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendar, locale]);

  // ---- Effacer le filtre actif (X rouge)
  const clearHeaderFilter = useCallback(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
    window.location.reload();
  }, []);

  // ---- Tooltip ancré sur l'évènement (au‑dessus, ou bascule en dessous si trop haut)
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const handleMouseOver = useCallback(
    (e) => {
      const evEl = e.target.closest(".sx__event");
      if (!evEl || !shellRef.current) return;

      // Métadonnées
      const domId =
        evEl.dataset?.eventId ||
        evEl.getAttribute("data-event-id") ||
        evEl.getAttribute("data-id") ||
        null;

      let meta = null;

      if (domId && eventsByIdRef.current.has(String(domId))) {
        meta = eventsByIdRef.current.get(String(domId));
      } else {
        // Fallback: match par titre (si titres uniques sur le jour)
        const aria = evEl.getAttribute("aria-label");
        const txt = aria || evEl.textContent?.trim() || "";
        const title = txt.replace(/\s*\([^)]+\)\s*$/, "");
        for (const v of eventsByIdRef.current.values()) {
          if (v.title === title) {
            meta = v;
            break;
          }
        }
      }
      if (!meta) return;

      const shellRect = shellRef.current.getBoundingClientRect();
      const evRect = evEl.getBoundingClientRect();

      // ancre horizontale au centre de l'event, verticale au bord haut
      let left = evRect.left - shellRect.left + evRect.width / 2;
      const top = evRect.top - shellRect.top;

      // clamp horizontal pour éviter de sortir
      left = clamp(left, 12, shellRect.width - 12);

      // si l'évènement est trop près du haut, on affiche le tooltip en dessous
      const thresholdTopPx = 60; // marge de sécurité
      const below = top < thresholdTopPx;

      setAnchor({ left, top, below });
      setTip({
        title: meta.title || "",
        client: meta.clientName || "",
        horse: meta.horseName || "",
        when: meta.whenLabel || fmtDateTime(meta.serviceDate, locale),
      });
    },
    [locale],
  );

  const handleMouseMove = useCallback((e) => {
    if (!shellRef.current) return;
    const overEvent = e.target.closest(".sx__event");
    if (!overEvent) {
      setTip(null);
      setAnchor(null);
    }
  }, []);

  const handleMouseOut = useCallback((e) => {
    const fromEvent = e.target.closest(".sx__event");
    const toEvent = e.relatedTarget && e.relatedTarget.closest?.(".sx__event");
    if (fromEvent && !toEvent) {
      setTip(null);
      setAnchor(null);
    }
  }, []);

  // Icône dynamique
  const getHeaderIcon = (type) => {
    switch (type) {
      case "client":
        return clientIcon;
      case "horse":
        return horseIcon;
      case "service":
        return serviceIcon;
      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* ===== Header ===== */}
        <div className={styles.header}>
          <h2 className={styles.title}>{t("calendar.title")}</h2>

          {/* Badge filtre actif (client / cheval / service) */}
          {headerName && (
            <div className={styles.headerBadge} title={t("calendar.clearFilterTooltip")}>
              {headerType && (
                <img
                  src={getHeaderIcon(headerType)}
                  alt={headerType}
                  className={styles.headerIcon}
                />
              )}
              <span className={styles.headerBadgeText}>{headerName}</span>
              <button
                type="button"
                className={styles.headerBadgeClose}
                aria-label={t("calendar.clearFilterAria")}
                onClick={clearHeaderFilter}>
                ×
              </button>
            </div>
          )}
        </div>

        {/* ===== Calendar + tooltip ===== */}
        <div
          ref={shellRef}
          className={styles.calendarShell}
          onMouseOver={handleMouseOver}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseOut}
          onMouseLeave={() => {
            setTip(null);
            setAnchor(null);
          }}>
          <ScheduleXCalendar calendarApp={calendar} />

          {tip && anchor && (
            <div
              className={anchor.below ? `${styles.tooltip} ${styles.tooltipBelow}` : styles.tooltip}
              role="tooltip"
              style={{ left: anchor.left, top: anchor.top }}>
              <div className={styles.tooltipTitle}>{tip.title}</div>
              {tip.client && (
                <div className={styles.tooltipMeta}>
                  {t("calendar.client")}: {tip.client}
                </div>
              )}
              {tip.horse && (
                <div className={styles.tooltipMeta}>
                  {t("calendar.horse")}: {tip.horse}
                </div>
              )}
              {tip.when && <div className={styles.tooltipMeta}>{tip.when}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
